import { chatAuthHeaders, getChatApiBase } from "@/apiBase";

export const CHAT_WEB_PUSH_PREF_KEY = "chat_web_push_enabled_v1";

export type ChatWebPushStatus =
  | "unsupported"
  | "denied"
  | "default"
  | "granted_off"
  | "subscribed"
  | "not_configured"
  | "error";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    out[i] = raw.charCodeAt(i);
  }
  return out;
}

export function isChatWebPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function readChatWebPushPref(): boolean {
  try {
    return localStorage.getItem(CHAT_WEB_PUSH_PREF_KEY) === "1";
  } catch {
    return false;
  }
}

export function persistChatWebPushPref(enabled: boolean): void {
  try {
    localStorage.setItem(CHAT_WEB_PUSH_PREF_KEY, enabled ? "1" : "0");
  } catch {
    /* ignore */
  }
}

async function chatPushFetch(
  suiteUserId: string | null,
  path: string,
  init?: RequestInit
): Promise<{ ok: boolean; data?: Record<string, unknown>; message?: string }> {
  const base = getChatApiBase();
  if (!base) return { ok: false, message: "API Chat indisponible." };
  const headers: Record<string, string> = {
    ...chatAuthHeaders(suiteUserId),
    ...(init?.headers as Record<string, string> | undefined),
  };
  if (init?.body && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  try {
    const res = await fetch(`${base.replace(/\/$/, "")}/${path.replace(/^\//, "")}`, {
      credentials: "include",
      ...init,
      headers,
    });
    const data = (await res.json()) as Record<string, unknown>;
    if (!res.ok || data.success === false) {
      return {
        ok: false,
        data,
        message: typeof data.message === "string" ? data.message : res.statusText,
      };
    }
    return { ok: true, data };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : "Erreur réseau" };
  }
}

export async function fetchChatVapidPublicKey(
  suiteUserId: string | null
): Promise<{ configured: boolean; publicKey: string | null }> {
  const r = await chatPushFetch(suiteUserId, "push-vapid-public.php");
  if (!r.ok || !r.data) return { configured: false, publicKey: null };
  return {
    configured: Boolean(r.data.configured),
    publicKey: typeof r.data.publicKey === "string" ? r.data.publicKey : null,
  };
}

async function waitForServiceWorkerReady(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

export async function getChatWebPushStatus(
  suiteUserId: string | null
): Promise<ChatWebPushStatus> {
  if (!isChatWebPushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  if (!readChatWebPushPref()) {
    return Notification.permission === "granted" ? "granted_off" : "default";
  }
  const vapid = await fetchChatVapidPublicKey(suiteUserId);
  if (!vapid.configured || !vapid.publicKey) return "not_configured";
  const reg = await waitForServiceWorkerReady();
  if (!reg) return "error";
  try {
    const sub = await reg.pushManager.getSubscription();
    return sub ? "subscribed" : "granted_off";
  } catch {
    return "error";
  }
}

export async function enableChatWebPush(
  suiteUserId: string | null
): Promise<{ ok: boolean; status: ChatWebPushStatus; message?: string }> {
  if (!isChatWebPushSupported()) {
    return { ok: false, status: "unsupported", message: "Navigateur non compatible." };
  }

  const permission =
    Notification.permission === "granted"
      ? "granted"
      : await Notification.requestPermission();
  if (permission !== "granted") {
    persistChatWebPushPref(false);
    return {
      ok: false,
      status: permission === "denied" ? "denied" : "default",
      message: "Permission notifications refusée.",
    };
  }

  const vapid = await fetchChatVapidPublicKey(suiteUserId);
  if (!vapid.configured || !vapid.publicKey) {
    return {
      ok: false,
      status: "not_configured",
      message: "Web Push non configuré côté serveur (VAPID).",
    };
  }

  const reg = await waitForServiceWorkerReady();
  if (!reg) {
    return { ok: false, status: "error", message: "Service worker indisponible." };
  }

  try {
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid.publicKey) as BufferSource,
      });
    }

    const json = sub.toJSON();
    const endpoint = json.endpoint;
    const p256dh = json.keys?.p256dh;
    const auth = json.keys?.auth;
    if (!endpoint || !p256dh || !auth) {
      return { ok: false, status: "error", message: "Abonnement Push incomplet." };
    }

    const save = await chatPushFetch(suiteUserId, "push-subscribe.php", {
      method: "POST",
      body: JSON.stringify({
        endpoint,
        keys: { p256dh, auth },
      }),
    });
    if (!save.ok) {
      return {
        ok: false,
        status: "error",
        message: save.message || "Enregistrement abonnement impossible.",
      };
    }

    persistChatWebPushPref(true);
    return { ok: true, status: "subscribed" };
  } catch (e) {
    return {
      ok: false,
      status: "error",
      message: e instanceof Error ? e.message : "Abonnement impossible.",
    };
  }
}

export async function disableChatWebPush(
  suiteUserId: string | null
): Promise<{ ok: boolean; status: ChatWebPushStatus; message?: string }> {
  persistChatWebPushPref(false);
  if (!isChatWebPushSupported()) {
    return { ok: true, status: "unsupported" };
  }
  try {
    const reg = await waitForServiceWorkerReady();
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      await chatPushFetch(suiteUserId, "push-unsubscribe.php", {
        method: "POST",
        body: JSON.stringify({ endpoint }),
      });
      try {
        await sub.unsubscribe();
      } catch {
        /* ignore */
      }
    }
  } catch {
    /* ignore */
  }
  return {
    ok: true,
    status: Notification.permission === "denied" ? "denied" : "granted_off",
  };
}

/** Resubscribe silencieux si préférence on + permission déjà granted. */
export async function syncChatWebPushOnBoot(suiteUserId: string | null): Promise<void> {
  if (!suiteUserId || !readChatWebPushPref()) return;
  if (!isChatWebPushSupported()) return;
  if (Notification.permission !== "granted") return;
  await enableChatWebPush(suiteUserId);
}

/**
 * Chat ouvert (desktop + PWA mobile) : ne pas laisser les toasts push à l’écran.
 * iOS ne signale souvent pas `client.focused` au SW — d’où le heartbeat.
 */
export function installChatPushFocusGuard(): () => void {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return () => undefined;
  }

  const post = (focused: boolean) => {
    try {
      navigator.serviceWorker.controller?.postMessage({
        type: "CHAT_CLIENT_FOCUS",
        focused,
      });
    } catch {
      /* ignore */
    }
  };

  const dismissVisible = () => {
    post(true);
    void navigator.serviceWorker.ready
      .then((reg) => reg.getNotifications())
      .then((list) => {
        for (const n of list) n.close();
      })
      .catch(() => undefined);
  };

  const sync = () => {
    if (document.visibilityState === "visible") dismissVisible();
    else post(false);
  };

  sync();
  const id = window.setInterval(() => {
    if (document.visibilityState === "visible") post(true);
  }, 8000);
  document.addEventListener("visibilitychange", sync);
  window.addEventListener("focus", sync);
  const onHub = (event: MessageEvent) => {
    const t = (event.data as { type?: string } | null)?.type;
    if (t === "AURELIA_CHAT_FRAME_HIDDEN" || t === "AURELIA_SUITE_LOGOUT") {
      post(false);
    }
  };
  window.addEventListener("message", onHub);
  return () => {
    window.clearInterval(id);
    document.removeEventListener("visibilitychange", sync);
    window.removeEventListener("focus", sync);
    window.removeEventListener("message", onHub);
    post(false);
  };
}
